import { Platform } from 'react-native';

/** "6:00 PM" -> [18, 0] */
const parseTime = (time: string): [number, number] => {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(time.trim());
  if (!match) {
    return [18, 0];
  }
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const suffix = match[3]?.toUpperCase();
  if (suffix === 'PM' && hours < 12) {
    hours += 12;
  }
  if (suffix === 'AM' && hours === 12) {
    hours = 0;
  }
  return [hours, minutes];
};

const pad = (n: number) => String(n).padStart(2, '0');
const stamp = (d: Date) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
const escapeText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

/**
 * An .ics with one daily event (a reminder) for the days of a challenge,
 * starting today at the chosen time — the web has no local notifications.
 */
export const buildReminderIcs = (title: string, time: string, days: number): string => {
  const [hours, minutes] = parseTime(time);
  const start = new Date();
  start.setHours(hours, minutes, 0, 0);
  const end = new Date(start.getTime() + 15 * 60 * 1000);
  const uid = `evoluno-${Date.now()}@evoluno.com`;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Evoluno//Challenges//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp(new Date())}`,
    `DTSTART:${stamp(start)}`,
    `DTEND:${stamp(end)}`,
    `RRULE:FREQ=DAILY;COUNT=${Math.max(1, days)}`,
    `SUMMARY:${escapeText(title)}`,
    'BEGIN:VALARM',
    'TRIGGER:PT0M',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeText(title)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

/**
 * The same daily reminder as a Google Calendar "create event" link: it opens
 * in a new tab with the event prefilled, no file to import.
 */
export const buildGoogleCalendarUrl = (title: string, time: string, days: number): string => {
  const [hours, minutes] = parseTime(time);
  const start = new Date();
  start.setHours(hours, minutes, 0, 0);
  const end = new Date(start.getTime() + 15 * 60 * 1000);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${stamp(start)}/${stamp(end)}`,
    recur: `RRULE:FREQ=DAILY;COUNT=${Math.max(1, days)}`,
    details: title,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/** web only: opens the Google Calendar event form in a new tab */
export const openGoogleCalendarReminder = (title: string, time: string, days: number): void => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return;
  }
  window.open(buildGoogleCalendarUrl(title, time, days), '_blank', 'noopener');
};

/** web only: hands the calendar file to the browser */
export const downloadReminderCalendar = (title: string, time: string, days: number): void => {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return;
  }
  const blob = new Blob([buildReminderIcs(title, time, days)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'evoluno-reminder.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
